/**
 * Presence REST client — thin wrapper over the Node backend's
 * /api/v1/presence/* endpoints (see api-contracts.md).
 *
 * Used for:
 *   - Initial seed before the Phoenix socket is up.
 *   - Mobile / REST-only callers setting their own status.
 *   - Mutating self status from non-channel surfaces (e.g., settings page).
 *
 * Live updates flow through the Phoenix socket (see `phoenix-socket.ts`).
 */

import type { PresenceInfo, PresenceStatus } from '../store/presence.store'
import { getSdkOrAppToken } from '@/lib/sdk-session-token'
import { isGuestSession } from '@/features/auth/services/guest-session'

// Presence is served by the global SDK server (which proxies to signaling).
// Falls back to the inMeet backend until `VITE_SDK_API_URL` is set at build time.
const API = import.meta.env.VITE_SDK_API_URL || import.meta.env.VITE_API_URL || ''

function getToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') ||
      sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } }
    const s = parsed?.session
    if (s?.expiresAt && s.expiresAt <= Date.now()) return ''
    return s?.accessToken || ''
  } catch {
    return ''
  }
}

async function send<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getSdkOrAppToken()
  // User-wide presence is an authenticated-user feature — it's keyed by a real
  // `iam.users` id. Skip the request entirely for guests, instead of firing one
  // that 401s (no token) or 502s (guest id not in iam.users). Two cases:
  //   1. No token at all (clean guest browser).
  //   2. This TAB joined as a guest, even though a logged-in session is shared
  //      from another tab via localStorage (per-tab flag in sessionStorage).
  // Callers already treat a thrown PresenceApiError as a no-op.
  if (!token || isGuestSession()) {
    throw new PresenceApiError('presence is unavailable for guests', 'NO_AUTH', 401)
  }
  const res = await fetch(`${API}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } }
    throw new PresenceApiError(
      body?.error?.message || `presence api ${res.status}`,
      body?.error?.code || 'API_ERROR',
      res.status,
    )
  }
  return res.json() as Promise<T>
}

export class PresenceApiError extends Error {
  code: string
  status: number
  constructor(message: string, code: string, status: number) {
    super(message)
    this.code = code
    this.status = status
  }
}

export interface UpdateMePayload {
  status?:        PresenceStatus | null
  customMessage?: string | null
  /** ISO 8601, in the future: when `customMessage` clears itself. Only
   *  accepted beside `customMessage`; leave it out (or null) for never. */
  customMessageExpiresAt?: string | null
}

/**
 * Cap per-request to keep the URL well under server/proxy limits.
 *
 * Each encoded UUID consumes ~39 bytes (`%2C` separator + 36 chars). At 50
 * per request the URL is < 2 KB — comfortable for any server we care about,
 * including Phoenix's Bandit default request-line cap. Requests are fanned
 * out in parallel; results stitched back in input order.
 */
const PRESENCE_BULK_CHUNK_SIZE = 50

function chunk<T>(arr: T[], size: number): T[][] {
  if (arr.length <= size) return [arr]
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

export const presenceApi = {
  /**
   * Presence for MY conversation peers — the server derives the set from the
   * auth token (no userIds sent). Used for the initial chat-screen seed; live
   * updates still flow through the per-user Phoenix topics.
   */
  async listMine(): Promise<{ presence: PresenceInfo[] }> {
    return send<{ presence: PresenceInfo[] }>(`/api/v1/presence`)
  },

  /** Bulk read for an explicit set (picker, mentions). Preserves order. */
  async list(userIds: string[]): Promise<{ presence: PresenceInfo[] }> {
    if (userIds.length === 0) return { presence: [] }

    const batches = chunk(userIds, PRESENCE_BULK_CHUNK_SIZE)
    const results = await Promise.all(
      batches.map((batch) => {
        const qs = encodeURIComponent(batch.join(','))
        return send<{ presence: PresenceInfo[] }>(`/api/v1/presence?userIds=${qs}`)
      }),
    )

    // Stitch batches back together preserving input order.
    return { presence: results.flatMap((r) => r.presence) }
  },

  /** My current snapshot. */
  me(): Promise<{ presence: PresenceInfo | null }> {
    return send<{ presence: PresenceInfo | null }>('/api/v1/presence/me')
  },

  /**
   * Update my status / custom message. Pass `null` to clear a field.
   * Server enforces 60/min rate limit.
   */
  setMe(payload: UpdateMePayload): Promise<{ presence: PresenceInfo }> {
    return send<{ presence: PresenceInfo }>('/api/v1/presence/me', {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },
}
